"use client";

import { useT } from "@/i18n/provider";
import { formatTimeAgo } from "@/i18n";
import { cn } from "@/lib/cn";

/**
 * Canonical relative-timestamp renderer for queue rows.
 *
 * Replaces the 6+ inline `formatTimeAgo(iso, t)` callsites scattered
 * across queue tables (Issues, Patches, Workspaces, Projects, Runs)
 * and custom row lists (Inbox, Follow-ups). Same component, same text
 * size, same colour, same hover-tooltip → operators always see "5m"
 * the same way and can hover for the absolute timestamp on every page.
 *
 *   <RelativeTime iso={row.created_at} />
 *   <RelativeTime iso={row.received_at} className="ml-auto" />
 *
 * Renders a semantic `<time dateTime>` element so screen readers and
 * indexers understand the value; the absolute timestamp lives in the
 * `title` attribute for hover discovery (matches the convention the
 * Issues table already used).
 *
 * `tone` defaults to `muted` (text-fg-muted) — the canonical density
 * tier for secondary metadata. Pass `tone="dim"` for the slightly
 * brighter "When" column where the timestamp is the focal point.
 */
export type RelativeTimeProps = {
  /** ISO 8601 timestamp string. */
  iso: string;
  /** Default `muted` for in-row metadata; `dim` when the timestamp is
   *  the column's focus (e.g. Workspaces "Updated" column). */
  tone?: "muted" | "dim";
  /** Default `xs` (10.5px) for in-row metadata; `sm` (11.5px) for
   *  dedicated time columns. */
  size?: "xs" | "sm";
  className?: string;
};

const TONE_CLS = {
  muted: "text-fg-muted",
  dim:   "text-fg-dim",
} as const;

const SIZE_CLS = {
  xs: "text-[10.5px]",
  sm: "text-[11.5px]",
} as const;

export function RelativeTime({
  iso,
  tone = "muted",
  size = "xs",
  className,
}: RelativeTimeProps) {
  const t = useT();
  const relative = formatTimeAgo(iso, t);
  // Absolute fallback for the hover tooltip — locale-aware. Caught
  // safely if the timestamp is malformed (we just render the raw
  // string then so the operator at least sees something).
  let absolute = iso;
  try { absolute = new Date(iso).toLocaleString(); } catch { /* noop */ }
  return (
    <time
      dateTime={iso}
      title={absolute}
      className={cn("tabular-nums whitespace-nowrap", TONE_CLS[tone], SIZE_CLS[size], className)}
    >
      {relative}
    </time>
  );
}
